import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Autocomplete, Chip, Alert, CircularProgress, Divider, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createUser, updateUser, listRoles, listWorkPoints, listUsers, listAdminClients, listAdminWarehouses, listAdminEquipment,
  type UserListItem, type RoleRef, type WorkPointRef, type ClientRef, type WarehouseRef, type EquipmentRef,
} from '../../api/admin'
import { errorMessage } from '../../api/client'

const schema = z.object({
  userName: z.string().min(3, 'Minimum 3 caractere').max(64),
  email: z.string().email('Email invalid'),
  firstName: z.string().min(1, 'Câmp obligatoriu'),
  lastName: z.string().min(1, 'Câmp obligatoriu'),
  phone: z.string().optional(),
  department: z.string().optional(),
  language: z.enum(['ro', 'en']),
  roleIds: z.array(z.string()).min(1, 'Selectați cel puțin un rol'),
  workPointIds: z.array(z.string()).min(1, 'Selectați cel puțin un punct de lucru'),
  clientIds: z.array(z.string()),
  warehouseIds: z.array(z.string()),
  equipmentIds: z.array(z.string()),
  delegateToUserId: z.string().optional(),
  delegateFrom: z.string().optional(),
  delegateUntil: z.string().optional(),
})
type FormValues = z.infer<typeof schema>

export default function UserFormDialog({ user, onClose }: { user: UserListItem | null | 'new'; onClose: () => void }) {
  const open = user !== null
  const editing = user !== null && user !== 'new'
  const qc = useQueryClient()

  const { data: roles } = useQuery({ queryKey: ['admin', 'roles'], queryFn: listRoles, enabled: open })
  const { data: workPoints } = useQuery({ queryKey: ['admin', 'work-points'], queryFn: listWorkPoints, enabled: open })
  const { data: clients } = useQuery({ queryKey: ['admin', 'clients'], queryFn: listAdminClients, enabled: open })
  const { data: warehouses } = useQuery({ queryKey: ['admin', 'warehouses'], queryFn: listAdminWarehouses, enabled: open })
  const { data: equipmentList } = useQuery({ queryKey: ['admin', 'equipment'], queryFn: listAdminEquipment, enabled: open })
  const { data: allUsers } = useQuery({ queryKey: ['admin', 'users', 'all'], queryFn: () => listUsers(), enabled: open && editing })
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userName: '', email: '', firstName: '', lastName: '', phone: '', department: '', language: 'ro',
      roleIds: [], workPointIds: [], clientIds: [], warehouseIds: [], equipmentIds: [],
      delegateToUserId: '', delegateFrom: '', delegateUntil: '',
    },
  })

  useEffect(() => {
    setPendingMessage(null)
    if (editing) {
      const current = user as UserListItem
      reset({
        userName: current.userName, email: current.email, firstName: current.firstName, lastName: current.lastName,
        phone: current.phone ?? '', department: current.department ?? '', language: current.language as 'ro' | 'en',
        roleIds: current.roles.map((r) => r.id), workPointIds: current.workPoints.map((w) => w.id),
        clientIds: current.clientPortfolio.map((c) => c.id), warehouseIds: current.warehouses.map((w) => w.id),
        equipmentIds: current.equipment.map((e) => e.id),
        delegateToUserId: current.delegateToUserId ?? '',
        delegateFrom: current.delegateFrom ? current.delegateFrom.slice(0, 10) : '',
        delegateUntil: current.delegateUntil ? current.delegateUntil.slice(0, 10) : '',
      })
    } else if (user === 'new') {
      reset({
        userName: '', email: '', firstName: '', lastName: '', phone: '', department: '', language: 'ro',
        roleIds: [], workPointIds: [], clientIds: [], warehouseIds: [], equipmentIds: [],
        delegateToUserId: '', delegateFrom: '', delegateUntil: '',
      })
    }
  }, [user, editing, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (editing) {
        const current = user as UserListItem
        return updateUser(current.id, {
          email: values.email, firstName: values.firstName, lastName: values.lastName,
          phone: values.phone || null, department: values.department || null, language: values.language,
          roleIds: values.roleIds, workPointIds: values.workPointIds,
          clientIds: values.clientIds, warehouseIds: values.warehouseIds, equipmentIds: values.equipmentIds,
          delegateToUserId: values.delegateToUserId || null,
          delegateFrom: values.delegateFrom || null,
          delegateUntil: values.delegateUntil || null,
        })
      }
      return createUser({
        userName: values.userName, email: values.email, firstName: values.firstName, lastName: values.lastName,
        phone: values.phone || null, department: values.department || null, language: values.language,
        roleIds: values.roleIds, workPointIds: values.workPointIds,
        clientIds: values.clientIds, warehouseIds: values.warehouseIds, equipmentIds: values.equipmentIds,
      })
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      if (data.pendingAdminApproval) {
        setPendingMessage('Adăugarea rolului Administrator așteaptă aprobarea unui alt administrator. Celelalte modificări au fost salvate.')
      } else {
        onClose()
      }
    },
  })

  const findRole = (id: string): RoleRef | undefined => roles?.find((r) => r.id === id)
  const findWp = (id: string): WorkPointRef | undefined => workPoints?.find((w) => w.id === id)
  const findClient = (id: string): ClientRef | undefined => clients?.find((c) => c.id === id)
  const findWarehouse = (id: string): WarehouseRef | undefined => warehouses?.find((w) => w.id === id)
  const findEquipment = (id: string): EquipmentRef | undefined => equipmentList?.find((e) => e.id === id)
  const otherUsers = editing ? (allUsers?.filter((u) => u.id !== (user as UserListItem).id) ?? []) : []
  const findUser = (id: string) => otherUsers.find((u) => u.id === id)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare utilizator' : 'Utilizator nou (invitație)'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {!editing && (
              <Alert severity="info" variant="outlined">
                Contul se creează fără parolă. Utilizatorul primește un email cu un link de
                activare valabil 72 de ore.
              </Alert>
            )}
            <Controller name="userName" control={control} render={({ field }) => (
              <TextField {...field} label="Nume utilizator" disabled={editing} fullWidth
                error={!!errors.userName} helperText={errors.userName?.message} />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="firstName" control={control} render={({ field }) => (
                <TextField {...field} label="Prenume" fullWidth error={!!errors.firstName} helperText={errors.firstName?.message} />
              )} />
              <Controller name="lastName" control={control} render={({ field }) => (
                <TextField {...field} label="Nume" fullWidth error={!!errors.lastName} helperText={errors.lastName?.message} />
              )} />
            </Stack>
            <Controller name="email" control={control} render={({ field }) => (
              <TextField {...field} label="Email" fullWidth error={!!errors.email} helperText={errors.email?.message} />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="phone" control={control} render={({ field }) => (
                <TextField {...field} label="Telefon" fullWidth />
              )} />
              <Controller name="department" control={control} render={({ field }) => (
                <TextField {...field} label="Departament" fullWidth />
              )} />
            </Stack>
            <Controller name="language" control={control} render={({ field }) => (
              <TextField {...field} select label="Limbă implicită" sx={{ width: 200 }} error={!!errors.language} helperText={errors.language?.message}>
                <MenuItem value="ro">Română</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </TextField>
            )} />
            <Controller name="roleIds" control={control} render={({ field }) => (
              <Autocomplete
                multiple options={roles?.map((r) => r.id) ?? []}
                getOptionLabel={(id) => findRole(id)?.name ?? id}
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                renderValue={(value, getItemProps) =>
                  value.map((id, index) => {
                    const { key, ...itemProps } = getItemProps({ index })
                    return <Chip key={key} {...itemProps} label={findRole(id)?.name ?? id} size="small" />
                  })}
                renderInput={(params) => (
                  <TextField {...params} label="Roluri" error={!!errors.roleIds} helperText={errors.roleIds?.message} />
                )}
              />
            )} />
            <Controller name="workPointIds" control={control} render={({ field }) => (
              <Autocomplete
                multiple options={workPoints?.map((w) => w.id) ?? []}
                getOptionLabel={(id) => findWp(id)?.name ?? id}
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                renderValue={(value, getItemProps) =>
                  value.map((id, index) => {
                    const { key, ...itemProps } = getItemProps({ index })
                    return <Chip key={key} {...itemProps} label={findWp(id)?.name ?? id} size="small" />
                  })}
                renderInput={(params) => (
                  <TextField {...params} label="Puncte de lucru" error={!!errors.workPointIds} helperText={errors.workPointIds?.message} />
                )}
              />
            )} />
            <Divider textAlign="left" sx={{ pt: 1 }}>
              <Typography variant="caption" color="text.secondary">Dimensiuni de restricție (FR-ADM-018, opțional)</Typography>
            </Divider>
            <Controller name="clientIds" control={control} render={({ field }) => (
              <Autocomplete
                multiple options={clients?.map((c) => c.id) ?? []}
                getOptionLabel={(id) => findClient(id)?.name ?? id}
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                renderValue={(value, getItemProps) =>
                  value.map((id, index) => {
                    const { key, ...itemProps } = getItemProps({ index })
                    return <Chip key={key} {...itemProps} label={findClient(id)?.name ?? id} size="small" />
                  })}
                renderInput={(params) => (
                  <TextField {...params} label="Portofoliu de clienți (agenți de vânzări)" />
                )}
              />
            )} />
            <Controller name="warehouseIds" control={control} render={({ field }) => (
              <Autocomplete
                multiple options={warehouses?.map((w) => w.id) ?? []}
                getOptionLabel={(id) => findWarehouse(id)?.name ?? id}
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                renderValue={(value, getItemProps) =>
                  value.map((id, index) => {
                    const { key, ...itemProps } = getItemProps({ index })
                    return <Chip key={key} {...itemProps} label={findWarehouse(id)?.name ?? id} size="small" />
                  })}
                renderInput={(params) => (
                  <TextField {...params} label="Gestiuni alocate" />
                )}
              />
            )} />
            <Controller name="equipmentIds" control={control} render={({ field }) => (
              <Autocomplete
                multiple options={equipmentList?.map((e) => e.id) ?? []}
                getOptionLabel={(id) => findEquipment(id)?.name ?? id}
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                renderValue={(value, getItemProps) =>
                  value.map((id, index) => {
                    const { key, ...itemProps } = getItemProps({ index })
                    return <Chip key={key} {...itemProps} label={findEquipment(id)?.name ?? id} size="small" />
                  })}
                renderInput={(params) => (
                  <TextField {...params} label="Utilaje/linii alocate (operatori)" />
                )}
              />
            )} />
            {editing && (
              <>
                <Divider textAlign="left" sx={{ pt: 1 }}>
                  <Typography variant="caption" color="text.secondary">Delegare de aprobare (concediu/absență)</Typography>
                </Divider>
                <Controller name="delegateToUserId" control={control} render={({ field }) => (
                  <Autocomplete
                    options={otherUsers.map((u) => u.id)}
                    getOptionLabel={(id) => { const u = findUser(id); return u ? `${u.firstName} ${u.lastName}` : id }}
                    value={field.value || null}
                    onChange={(_, v) => field.onChange(v ?? '')}
                    renderInput={(params) => <TextField {...params} label="Aprobator înlocuitor" />}
                  />
                )} />
                <Stack direction="row" spacing={2}>
                  <Controller name="delegateFrom" control={control} render={({ field }) => (
                    <TextField {...field} type="date" label="Delegare de la" fullWidth slotProps={{ inputLabel: { shrink: true } }} />
                  )} />
                  <Controller name="delegateUntil" control={control} render={({ field }) => (
                    <TextField {...field} type="date" label="Delegare până la" fullWidth slotProps={{ inputLabel: { shrink: true } }} />
                  )} />
                </Stack>
              </>
            )}
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {pendingMessage && <Alert severity="info">{pendingMessage}</Alert>}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          {pendingMessage ? (
            <Button onClick={onClose} variant="contained">Închide</Button>
          ) : (
            <>
              <Button onClick={onClose}>Renunță</Button>
              <Button type="submit" variant="contained" disabled={mutation.isPending}
                startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
                {editing ? 'Salvează' : 'Trimite invitația'}
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
}
